import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Goal } from '@/types/experiment';

interface CreateExperimentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goals: Goal[];
  onCreateExperiment: (experimentData: { 
    name: string; 
    goalId?: string;
    hypothesis: string; 
    parameters: Record<string, any> 
  }) => void;
}

export const CreateExperimentDialog: React.FC<CreateExperimentDialogProps> = ({
  open,
  onOpenChange,
  goals,
  onCreateExperiment
}) => {
  const [formData, setFormData] = useState({
    name: '',
    goalId: '',
    hypothesis: '',
    parameters: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name.trim()) {
      let parsedParameters = {};
      try {
        parsedParameters = formData.parameters ? JSON.parse(formData.parameters) : {};
      } catch (error) {
        // If JSON parsing fails, store as a simple object
        parsedParameters = { raw: formData.parameters };
      }
      
      onCreateExperiment({
        name: formData.name,
        goalId: formData.goalId || undefined,
        hypothesis: formData.hypothesis || '',
        parameters: parsedParameters
      });
      
      setFormData({ name: '', goalId: '', hypothesis: '', parameters: '' });
    }
  };

  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create New Experiment</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="experimentName">Experiment Name</Label>
            <Input
              id="experimentName"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="e.g., Test new feature extraction method"
              required
            />
          </div>
          
          <div>
            <Label htmlFor="goalSelect">Associated Goal (Optional)</Label>
            <Select value={formData.goalId} onValueChange={(value) => 
              setFormData(prev => ({ ...prev, goalId: value === 'none' ? '' : value }))
            }>
              <SelectTrigger>
                <SelectValue placeholder="Select a goal or leave unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-muted-foreground">🎯 No goal (unassigned)</span>
                </SelectItem>
                {goals.map((goal) => (
                  <SelectItem key={goal.id} value={goal.id}>
                    {goal.name}
                    {goal.description && goal.description !== goal.name && (
                      <span className="text-muted-foreground ml-2">- {goal.description}</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              You can assign a goal later or leave this experiment unassigned
            </p>
          </div>
          
          <div>
            <Label htmlFor="hypothesis">Hypothesis</Label>
            <Textarea
              id="hypothesis"
              value={formData.hypothesis}
              onChange={handleChange('hypothesis')}
              placeholder="What do you expect to happen? (optional)"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="parameters">Parameters (JSON)</Label>
            <Textarea
              id="parameters"
              value={formData.parameters}
              onChange={handleChange('parameters')}
              placeholder='{"learning_rate": 0.01, "batch_size": 32}'
              rows={3}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Optional: Enter experiment parameters as JSON
            </p>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={!formData.name.trim()}
            >
              Create Experiment
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
